import { useState, useCallback } from 'react';
import type { DualSwotData, SwotQuadrants, SwotItem } from './buildPortalViewModel';
import { getDisplayLabel, getBenefit, getTechnicalDetail } from '../../mapping/jargonMapping';
import { useExpertView } from '../../context/ExpertViewContext';

interface DualSwotGridProps {
  data: DualSwotData;
  highlightedNodeId?: string | null;
  onNodeClick?: (nodeId: string, grid: 'statusQuo' | 'recommended') => void;
}

export function DualSwotGrid({ data, highlightedNodeId, onNodeClick }: DualSwotGridProps) {
  const [expandedNode, setExpandedNode] = useState<string | null>(null);
  const { isExpertView } = useExpertView();

  const handleNodeClick = useCallback((nodeId: string, grid: 'statusQuo' | 'recommended') => {
    setExpandedNode(prev => prev === nodeId ? null : nodeId);
    onNodeClick?.(nodeId, grid);
  }, [onNodeClick]);

  return (
    <section className="dual-swot" aria-label="System analysis">
      <h2 className="dual-swot__heading">How things compare</h2>
      <div className="dual-swot__grids">
        <SwotGridPanel
          title="Your current system"
          variant="status-quo"
          quadrants={data.statusQuo}
          highlightedNodeId={highlightedNodeId}
          expandedNode={expandedNode}
          onNodeClick={(id) => handleNodeClick(id, 'statusQuo')}
          isExpertView={isExpertView}
        />
        <SwotGridPanel
          title="With the recommended solution"
          variant="recommended"
          quadrants={data.recommended}
          highlightedNodeId={highlightedNodeId}
          expandedNode={expandedNode}
          onNodeClick={(id) => handleNodeClick(id, 'recommended')}
          isExpertView={isExpertView}
        />
      </div>
    </section>
  );
}

// ─── Single SWOT Grid Panel ──────────────────────────────────────

interface SwotGridPanelProps {
  title: string;
  variant: 'status-quo' | 'recommended';
  quadrants: SwotQuadrants;
  highlightedNodeId?: string | null;
  expandedNode: string | null;
  onNodeClick: (nodeId: string) => void;
  isExpertView: boolean;
}

function SwotGridPanel({ title, variant, quadrants, highlightedNodeId, expandedNode, onNodeClick, isExpertView }: SwotGridPanelProps) {
  const quadrantOrder: Array<{ key: keyof SwotQuadrants; label: string; color: string }> =
    variant === 'status-quo'
      ? [
          { key: 'weaknesses', label: 'Weaknesses', color: 'red' },
          { key: 'threats', label: 'Threats', color: 'amber' },
          { key: 'strengths', label: 'Strengths', color: 'green' },
          { key: 'opportunities', label: 'Opportunities', color: 'blue' },
        ]
      : [
          { key: 'strengths', label: 'Strengths', color: 'green' },
          { key: 'opportunities', label: 'Opportunities', color: 'blue' },
          { key: 'weaknesses', label: 'Weaknesses', color: 'red' },
          { key: 'threats', label: 'Threats', color: 'amber' },
        ];

  return (
    <div className={`swot-panel swot-panel--${variant}`}>
      <h3 className="swot-panel__title">{title}</h3>
      <div className="swot-panel__quadrants">
        {quadrantOrder.map(({ key, label, color }) => {
          const items = quadrants[key];
          if (items.length === 0) return null;

          // Dynamic sizing: more items = more visual weight
          const weight = items.length <= 2 ? 1 : items.length <= 4 ? 1.5 : 2;

          return (
            <div
              key={key}
              className={`swot-panel__quadrant swot-panel__quadrant--${color}`}
              style={{ flexGrow: weight }}
            >
              <h4 className="swot-panel__quadrant-label">{label}</h4>
              <div className="swot-panel__nodes">
                {items.map(item => (
                  <SwotNode
                    key={item.id}
                    item={item}
                    isHighlighted={highlightedNodeId === item.id}
                    isExpanded={expandedNode === item.id}
                    onClick={() => onNodeClick(item.id)}
                    isExpertView={isExpertView}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── SWOT Node ───────────────────────────────────────────────────

function SwotNode({ item, isHighlighted, isExpanded, onClick, isExpertView }: {
  item: SwotItem;
  isHighlighted: boolean;
  isExpanded: boolean;
  onClick: () => void;
  isExpertView: boolean;
}) {
  // Try to get human-friendly label from mapping
  const displayLabel = isExpertView ? item.label : (getDisplayLabel(item.id) ?? item.label);
  const benefit = getBenefit(item.id);
  const technicalDetail = getTechnicalDetail(item.id);

  return (
    <div className={`swot-node-pill ${isHighlighted ? 'swot-node-pill--highlighted' : ''}`}>
      <button
        className="swot-node-pill__button"
        onClick={onClick}
        aria-expanded={isExpanded}
        aria-label={displayLabel}
      >
        {item.icon && <span className="swot-node-pill__icon" aria-hidden="true">{item.icon}</span>}
        <span className="swot-node-pill__label">{displayLabel}</span>
      </button>
      {isExpanded && (
        <div className="swot-node-pill__detail" role="region" aria-label={`Detail for ${displayLabel}`}>
          {/* Show benefit if in human-friendly mode and available */}
          {!isExpertView && benefit && (
            <p style={{ marginBottom: '0.5rem' }}>
              <strong>Benefit:</strong> {benefit}
            </p>
          )}
          
          {/* Show detail from item or from mapping */}
          {item.detail && <p>{item.detail}</p>}
          
          {/* Show technical detail in small grey text */}
          {!isExpertView && technicalDetail && (
            <p style={{ fontSize: '0.75rem', color: 'var(--portal-text-muted)', marginTop: '0.5rem' }}>
              Technical: {technicalDetail}
            </p>
          )}
          
          {item.resolvedBy && (
            <p className="swot-node-pill__resolved">
              Addressed by: Option {item.resolvedBy === 'a' ? 'A' : 'A+'}
            </p>
          )}
          {item.evidenceBasis && (
            <p className="swot-node-pill__evidence">
              Evidence: {item.evidenceBasis}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
